import type { CompRef } from '@wix/document-services-types';
import type { EditorAPI } from '@/editorAPI';
import { isGhostRefComponent as isGhostRefComponentByType } from '@/documentServices';
import { CompPointersApiKey } from '#packages/compPointers';
import { platform } from '../index';

export const isGhostCollapsed = (editorAPI: EditorAPI, compRef: CompRef) => {
  const properties = editorAPI.dsRead.components.properties.get(compRef);
  return properties?.ghost === 'COLLAPSED';
};

const isComponentHidden = (editorAPI: EditorAPI, compRef: CompRef) => {
  const compPointersApi = editorAPI.host.getAPI(CompPointersApiKey);
  const compPointer = compPointersApi.withCurrentContext(compRef);
  const layouts = editorAPI.dsRead.components.responsiveLayout.get(compPointer);
  if (!layouts) {
    return false;
  }
  return layouts.componentLayout?.hidden;
};

export const isRendered = (editorAPI: EditorAPI, compRef: CompRef) => {
  return editorAPI.dsRead.components.is.rendered(compRef);
};

export const hasVolume = (editorAPI: EditorAPI, compRef: CompRef) => {
  const { width, height } =
    editorAPI.dsRead.components.layout.getRelativeToScreen(compRef);
  return width > 0 && height > 0;
};

export const getRepeatedComponents = (
  editorAPI: EditorAPI,
  compRef: CompRef,
) => {
  return editorAPI.dsRead.deprecatedOldBadPerformanceApis.components.getRepeatedComponents(
    compRef,
  );
};

export const isComponentVisibleInLayersPanel = (
  editorAPI: EditorAPI,
  compRef: CompRef,
): boolean =>
  !isComponentHidden(editorAPI, compRef) &&
  !isGhostCollapsed(editorAPI, compRef) &&
  !isGhostRefComponentByType(editorAPI.dsRead, compRef);

const getRepeatedCompRefs = (
  compRef: CompRef,
  editorAPI: EditorAPI,
): CompRef[] => {
  if (platform.renderUtils.isGhostCollapsed(editorAPI, compRef)) {
    return [];
  }
  const componentsRefs: CompRef[] = [];
  const isComponentRendered = platform.renderUtils.isRendered(
    editorAPI,
    compRef,
  );
  if (isComponentRendered) {
    componentsRefs.push(compRef);
  } else {
    componentsRefs.push(
      ...platform.renderUtils.getRepeatedComponents(editorAPI, compRef),
    );
  }
  if (componentsRefs.length === 0) {
    return [];
  }
  const hasVolume = platform.renderUtils.hasVolume(
    editorAPI,
    componentsRefs[0],
  );
  return hasVolume ? componentsRefs : [];
};

export const getCompRefIncludingRepeatedItems = (
  editorAPI: EditorAPI,
  compRefs: CompRef[],
) => {
  return compRefs.flatMap((compRef: CompRef) =>
    getRepeatedCompRefs(compRef, editorAPI),
  );
};
